// src/oura/components/OuraErrorBoundary.tsx
//
// Containment for the Oura surface (#3513).
//
// A single unguarded lookup in the Pages workshop history took the entire
// document white — nav, chrome and all — because nothing in the Oura tree
// caught it and React unmounts everything when a render throws. The staff
// member lost whatever they were mid-way through, and the only recovery was a
// browser reload.
//
// This does not make that class of bug acceptable; the specific one is fixed at
// its source. It makes the NEXT one survivable, which matters because the FE
// types here are hand-written mirrors of an evolving backend contract, so
// "the payload grew a value we do not know about" is a recurring shape rather
// than a one-off.

import React from "react";
import { AlertTriangleIcon } from "lucide-react";
import { Button } from "@component/button";
import { catchErrorLog } from "@util/sentry";

interface Props {
    children: React.ReactNode;
}

interface State {
    error: Error | null;
}

export class OuraErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        // The component stack is the part that actually locates the crash; the
        // message alone rarely says which panel died.
        catchErrorLog(
            `[oura] render crashed: ${error.message}\ncomponent stack:${info.componentStack ?? " unavailable"}`,
        );
    }

    render() {
        if (!this.state.error) return this.props.children;

        return (
            <div className="flex min-h-[60vh] items-center justify-center p-6">
                <div className="max-w-md text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <AlertTriangleIcon className="h-6 w-6" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">This screen hit an error</h2>
                    <p className="mt-1.5 text-sm text-gray-600">
                        Nothing you did caused it and nothing was lost. The team has been notified.
                    </p>
                    {/* Reset re-renders the same route: enough for a transient
                        payload problem, and cheaper than losing the whole tab.
                        Reload stays available for the case where it is not. */}
                    <div className="mt-5 flex items-center justify-center gap-3">
                        <Button onClick={() => this.setState({ error: null })}>Try again</Button>
                        <Button variant="outline" onClick={() => window.location.reload()}>
                            Reload the page
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}
